import { Box, IconButton } from "@mui/material"
import { DraftOutlined, KeyboardArrowDownOutlined, KeyboardArrowUpOutlined } from "@mui-symbols-material/w300"
import type { ColumnDef } from "@tanstack/react-table"
import dayjs from "dayjs"

import { InventoryProductDescriptionCell, InventoryProductIdCell } from "../../../components"
import { ADJUSTMENT_REASON_TO_LABEL } from "../../Adjustment/constants"
import {
  AdjustmentHistoryQuantityChangeCell,
  AdjustmentHistoryRowActions,
  AdjustmentHistoryStatusCell,
} from "../components/AdjustmentHistoryTable"
import { AdjustmentTypeChip } from "../components/AdjustmentTypeChip"
import { ADJUSTMENT_TYPES_WITH_WORK_ORDERS } from "../constants"

import { DATE_FORMAT } from "@/constants/formats"
import type { AdjustmentHistoryItem } from "@/graphql/codegen/graphql"
import { InventoryAdjustmentType } from "@/graphql/codegen/graphql"

interface UseAdjustmentHistoryColumnsProps {
  canMaintain: boolean
  isPrinting: boolean
  onDocumentsOpen: (item: AdjustmentHistoryItem) => void
  onWorkOrderConfirm: (item: AdjustmentHistoryItem) => Promise<void>
  onWorkOrderPrint: (item: AdjustmentHistoryItem) => void
  onPalletLabelPrintAll: (item: AdjustmentHistoryItem) => void
}

export const useAdjustmentHistoryColumns = ({
  canMaintain,
  isPrinting,
  onDocumentsOpen,
  onWorkOrderConfirm,
  onWorkOrderPrint,
  onPalletLabelPrintAll,
}: UseAdjustmentHistoryColumnsProps): ColumnDef<AdjustmentHistoryItem>[] => {
  return [
    {
      accessorKey: "date",
      header: "Date",
      cell: ({ row }) => dayjs(row.original.date).format(DATE_FORMAT.US),
      enableSorting: true,
    },
    {
      accessorKey: "adjustmentReferenceNbr",
      header: "Adjustment #",
      cell: ({ row }) => row.original.adjustmentReferenceNbr,
      enableSorting: false,
    },
    {
      accessorKey: "warehouse",
      header: "Warehouse",
      cell: ({ row }) => [row.original.warehouse.as400Id, row.original.warehouse.description].filter(Boolean).join(" "),
      enableSorting: true,
    },
    {
      accessorKey: "productId",
      header: "Product ID",
      cell: ({ row }) => <InventoryProductIdCell product={row.original.product} />,
      enableSorting: false,
    },
    {
      accessorKey: "description",
      header: "Description",
      cell: ({ row }) => <InventoryProductDescriptionCell product={row.original.product} />,
      enableSorting: false,
    },
    {
      accessorKey: "adjustmentType",
      header: "Type",
      cell: ({ row }) => <AdjustmentTypeChip type={row.original.adjustmentType} />,
      enableSorting: false,
    },
    {
      accessorKey: "totalQtyChange",
      header: "Total Qty Change",
      cell: ({ row }) => <AdjustmentHistoryQuantityChangeCell qtyChange={row.original.totalQtyChange} />,
      enableSorting: false,
    },
    {
      accessorKey: "reason",
      header: "Reason",
      cell: ({ row }) => {
        const { adjustmentType, reason } = row.original
        if (![InventoryAdjustmentType.AdjustQuantity, InventoryAdjustmentType.AddNewPallets].includes(adjustmentType))
          return "N/A"

        return reason ? ADJUSTMENT_REASON_TO_LABEL[reason] : "N/A"
      },
      enableSorting: false,
    },
    {
      accessorKey: "adjustedBy",
      header: "Adjusted By",
      cell: ({ row }) => row.original.adjustedBy?.displayName ?? "",
      enableSorting: true,
    },
    {
      accessorKey: "status",
      header: "Status",
      cell: ({ row }) => <AdjustmentHistoryStatusCell status={row.original.status} />,
      enableSorting: true,
    },
    {
      accessorKey: "docs",
      header: "Docs",
      size: 56,
      enableSorting: false,
      cell: ({ row }) =>
        row.original.hasDocuments ? (
          <Box className='flex items-center justify-center'>
            <IconButton size='small' onClick={() => onDocumentsOpen(row.original)}>
              <DraftOutlined />
            </IconButton>
          </Box>
        ) : null,
    },
    {
      accessorKey: "actions",
      header: "",
      size: 112,
      enableSorting: false,
      cell: ({ row }) => (
        <Box className='flex items-center justify-end gap-x-2'>
          {ADJUSTMENT_TYPES_WITH_WORK_ORDERS.includes(row.original.adjustmentType) && (
            <AdjustmentHistoryRowActions
              item={row.original}
              canMaintain={canMaintain}
              isPrinting={isPrinting}
              onWorkOrderConfirm={onWorkOrderConfirm}
              onWorkOrderPrint={onWorkOrderPrint}
              onPalletLabelPrintAll={onPalletLabelPrintAll}
            />
          )}
          <IconButton size='small' onClick={row.getToggleExpandedHandler()}>
            {row.getIsExpanded() ? <KeyboardArrowUpOutlined /> : <KeyboardArrowDownOutlined />}
          </IconButton>
        </Box>
      ),
    },
  ]
}
